import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, Trash, Plus, CheckCircle, WarningCircle, Browser } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#00A0D2';

const WPBAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [ncAccounts, setNcAccounts] = useState([]);
  const [busy, setBusy] = useState('');
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ label: '', cf_api_token: '', cf_account_id: '', server_ip: '', namecheap_account_id: '', cpanel_host: '', cpanel_user: '', cpanel_api_token: '' });

  const load = async () => {
    try {
      const [a, nc] = await Promise.all([
        axios.get(`${API}/api/wp-builder/accounts`, { withCredentials: true }),
        axios.get(`${API}/api/namecheap/accounts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAccounts(a.data); setNcAccounts(nc.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.label || !form.cf_api_token || !form.cf_account_id || !form.server_ip) { toast.error('Label, Cloudflare token, Account ID and server IP are required'); return; }
    setBusy('create');
    try {
      const payload = { ...form, namecheap_account_id: form.namecheap_account_id || null };
      await axios.post(`${API}/api/wp-builder/accounts`, payload, { withCredentials: true });
      toast.success('Account added'); setShow(false);
      setForm({ label: '', cf_api_token: '', cf_account_id: '', server_ip: '', namecheap_account_id: '', cpanel_host: '', cpanel_user: '', cpanel_api_token: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };
  const test = async (id) => { setBusy(id + 'test'); try { const r = await axios.post(`${API}/api/wp-builder/accounts/${id}/test`, {}, { withCredentials: true }); toast.success(`Cloudflare ${r.data.cloudflare} · Namecheap ${r.data.namecheap}`); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); } };
  const del = async (id) => { if (!window.confirm('Delete this builder account?')) return; setBusy(id + 'del'); try { await axios.delete(`${API}/api/wp-builder/accounts/${id}`, { withCredentials: true }); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); } finally { setBusy(''); } };

  const F = ({ k, ph, type = 'text' }) => (
    <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph} type={type} data-testid={`wpb-acc-${k}`} className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm focus:outline-none focus:border-[#00A0D2] w-full" />
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="text-4xl font-black mb-2" style={{ color: T }}>BUILDER ACCOUNTS</h1>
          <p className="text-slate-400 font-mono">Cloudflare + server IP + cPanel used to build sites.</p></div>
        <button onClick={() => setShow(!show)} data-testid="wpb-add-account-btn" className="flex items-center gap-2 text-white px-4 py-2.5 font-bold text-sm uppercase tracking-wider" style={{ background: T }}><Plus size={16} /> Add Account</button>
      </div>

      {show && (
        <div className="bg-card border border-white/10 p-6 space-y-4" data-testid="wpb-account-form">
          <div className="grid md:grid-cols-2 gap-3">
            <F k="label" ph="Label (e.g. Main hosting)" />
            <F k="server_ip" ph="Server IP for A records (your cPanel IP)" />
            <F k="cf_api_token" ph="Cloudflare API Token (Zone+DNS+Cache Purge)" />
            <F k="cf_account_id" ph="Cloudflare Account ID" />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Namecheap account (for nameservers)</label>
            <select value={form.namecheap_account_id} onChange={e => setForm({ ...form, namecheap_account_id: e.target.value })} data-testid="wpb-acc-nc-select" className="w-full bg-[#111318] border border-white/10 p-2.5 text-white text-sm mt-1">
              <option value="">Auto — first active Namecheap account</option>
              {ncAccounts.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
          </div>
          <div className="pt-2 border-t border-white/5">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">cPanel / Softaculous (optional now, needed for WordPress install)</div>
            <div className="grid md:grid-cols-3 gap-3">
              <F k="cpanel_host" ph="cPanel host (e.g. server.host.com)" />
              <F k="cpanel_user" ph="cPanel username" />
              <F k="cpanel_api_token" ph="cPanel API token" />
            </div>
          </div>
          <button onClick={create} disabled={busy === 'create'} data-testid="wpb-save-account-btn" className="flex items-center gap-2 text-white px-4 py-2.5 font-bold text-sm uppercase tracking-wider" style={{ background: T }}>{busy === 'create' ? <CircleNotch size={16} className="animate-spin" /> : <Plus size={16} />} Save Account</button>
        </div>
      )}

      <div className="grid gap-4">
        {accounts.map(a => (
          <div key={a.id} className="bg-card border border-white/10 p-6" data-testid={`wpb-account-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3"><Browser size={26} style={{ color: T }} className="mt-1" />
                <div>
                  <div className="font-bold text-white text-lg flex items-center gap-2">{a.label}
                    {a.status === 'ready' && <CheckCircle size={16} className="text-[#00E676]" />}
                    {a.status === 'error' && <WarningCircle size={16} className="text-red-400" />}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-1">CF: {a.cf_token_masked} · acct {a.cf_account_id?.slice(0, 8)}… · IP {a.server_ip}</div>
                  <div className="text-xs text-slate-500 font-mono">cPanel: {a.has_cpanel ? `${a.cpanel_user}@${a.cpanel_host}` : 'not configured'}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => test(a.id)} disabled={busy.startsWith(a.id)} data-testid={`wpb-test-${a.id}`} className="border border-white/15 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-white/5 disabled:opacity-50">{busy === a.id + 'test' ? <CircleNotch size={14} className="animate-spin" /> : 'Test'}</button>
                <button onClick={() => del(a.id)} disabled={busy.startsWith(a.id)} data-testid={`wpb-del-${a.id}`} className="bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-red-500 hover:text-white"><Trash size={14} /></button>
                <CopyApiButton provider="wp-builder" accountId={a.id} />
              </div>
            </div>
          </div>
        ))}
        {accounts.length === 0 && <div className="text-slate-500 text-center py-10">No builder accounts yet. Add one to get started.</div>}
      </div>
    </div>
  );
};
export default WPBAccounts;
